import { FC, useCallback, useMemo, useState } from 'react';
import s from '../../Projects.module.scss';
import { ScrollableContainer } from 'components/common/ScrollableContainer';
import { Button } from 'components/common/Button';
import { ReactComponent as FolderWithPlus } from 'images/folderWithPlus.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { selectProjects } from 'store/features/project/selectors';
import { useDeleteProjectMutation } from 'store/features/project/project-api';
import { useQueryError } from 'lib/hooks/useQueryError';
import { Loader } from 'components/common/Loader/Loader';
import { Item } from '../Item/Item';
import { useTranslation } from 'react-i18next';
import { ProjectEditor } from '../ProjectEditor/ProjectEditor';
import { useToggle } from 'lib/hooks/useToggle';
import { ErrorModal } from 'components/common/ErrorModal/ErrorModal';
import { Project as ProjectType } from 'types/project';
import { NewProject } from '../NewProject/NewProject';

const icons = {
  folder: <FolderWithPlus className={s['icon-folder']} />,
};

export const ProjectList: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { chatId } = useParams();

  const projects = useAppSelector(selectProjects);

  const [
    deleteProject,
    { isLoading: isDeleteLoading, isError: isDeleteError, error: deleteError },
  ] = useDeleteProjectMutation();

  const { errorMessage, closeError, showError } = useQueryError(
    isDeleteError,
    deleteError,
  );

  const [isOpenNewProject, setIsOpenNewProject] = useToggle(false);
  const [editProjectId, setEditProjectId] = useState<ProjectType['id'] | null>(
    null,
  );

  const project = useMemo(() => {
    return projects.find((item) => item.id === editProjectId);
  }, [projects, editProjectId]);

  const handleDeleteProject = useCallback(
    async (projectId: string) => {
      if (isDeleteLoading) return;
      await deleteProject(projectId);
    },
    [isDeleteLoading, deleteProject],
  );

  const handleOpenProject = useCallback(
    (id: string) => {
      const basePath = chatId ? `/chat/${chatId}` : '';

      navigate(`${basePath}/project/${id}`);
    },
    [navigate, chatId],
  );

  const handleNewProject = useCallback(() => {
    setIsOpenNewProject(true);
  }, [setIsOpenNewProject]);

  const handleCloseNewProject = useCallback(() => {
    setIsOpenNewProject(false);
  }, [setIsOpenNewProject]);

  const handleEditProject = useCallback((projectId: string) => {
    setEditProjectId(projectId);
  }, []);

  const handleCloseEditProject = useCallback(() => {
    setEditProjectId(null);
  }, []);

  return (
    <>
      <ScrollableContainer className={s.scroll}>
        <ul className={s.list}>
          <li>
            <Button
              className={s['new-project__btn']}
              onClick={handleNewProject}
            >
              {icons.folder}
              <span className={s['icon-folder__text']}>{t('project.new')}</span>
            </Button>
          </li>

          {projects.map((projectItem) => (
            <Item
              key={projectItem.id}
              projectId={projectItem.id}
              name={projectItem.name}
              onClick={handleOpenProject}
              onDelete={handleDeleteProject}
              onEdit={handleEditProject}
            />
          ))}
        </ul>
      </ScrollableContainer>

      {isOpenNewProject && <NewProject onClose={handleCloseNewProject} />}

      {editProjectId && (
        <ProjectEditor
          projectId={editProjectId}
          name={project?.name ?? ''}
          instruction={project?.instruction ?? ''}
          files={project?.files ?? []}
          onClose={handleCloseEditProject}
        />
      )}

      {isDeleteLoading && <Loader />}
      {showError && <ErrorModal message={errorMessage} onClose={closeError} />}
    </>
  );
};
